import { Allotment, AllotmentHandle, AllotmentProps, LayoutPriority } from "allotment";
import { CommonProps } from "allotment/dist/types/src/allotment";
import React, { Children, useLayoutEffect, useRef, useState } from "react";
import { CollapsedHandle } from "./CollapsedHandle";
import styles from "./ResizablePanels.module.css";
import "allotment/dist/style.css";

export interface ResizablePanelsOptions extends AllotmentProps {
  hideSashOnSnap?: boolean;
  withAnimation?: boolean;
  firstPanel: {
    visible?: boolean,
    preferredSize?: string | number | undefined;
    priority?: LayoutPriority | undefined;
    onVisibilityChange?: (visible: boolean) => void;
  } & CommonProps;
  secondPanel: {
    visible?: boolean,
    preferredSize?: string | number | undefined;
    priority?: LayoutPriority | undefined;
    onVisibilityChange?: (visible: boolean) => void;
  } & CommonProps;
}

export const ResizablePanels = ({ firstPanel, secondPanel, hideSashOnSnap, withAnimation, children, ...options }: ResizablePanelsOptions) => {
  const [sashHidden, setSashHidden] = useState(false);
  const ref = useRef<AllotmentHandle>(null);

  const kids = Children.toArray(children);

  if (kids.length !== 2)
    throw new Error(`ResizablePanels component must be composed of 2 panels. Number of panels provided: ${kids.length}`);

  useLayoutEffect(() => {
    if (hideSashOnSnap) {
      if (firstPanel.visible === false || secondPanel.visible === false) {
        setSashHidden(true)
      }
      else
        setSashHidden(false)
    }

  }, [firstPanel.visible, secondPanel.visible, hideSashOnSnap, withAnimation])

  return (
    <div className={options.vertical ? styles.mainContainerVertical : styles.mainContainer}>
      <CollapsedHandle position={options.vertical ? "up" : "left"} visible={hideSashOnSnap === true && !firstPanel.visible} onClick={() => firstPanel && firstPanel.onVisibilityChange && firstPanel.onVisibilityChange(true)} />
      <Allotment
        {...options}
        ref={ref}
        className={[options.className ?? "", styles.container, sashHidden ? styles.sashHidden : ""].filter((item) => item).join(" ")}
        onVisibleChange={(index: number, visible: boolean) => {
          if (index === 0 && firstPanel.onVisibilityChange)
            firstPanel.onVisibilityChange(visible);
          if (index === 1 && secondPanel.onVisibilityChange)
            secondPanel.onVisibilityChange(visible);

          options.onVisibleChange && options.onVisibleChange(index, visible);
        }}>
        <Allotment.Pane
          {...firstPanel}
          className={[firstPanel.className, styles.panel].join(" ")}
          visible={firstPanel.visible ?? true}
          preferredSize={firstPanel.preferredSize}
          priority={firstPanel.priority}
          children={kids[0]} />
        <Allotment.Pane
          {...secondPanel}
          className={[secondPanel.className, styles.panel].join(" ")}
          visible={secondPanel.visible ?? true}
          preferredSize={secondPanel.preferredSize}
          priority={secondPanel.priority}
          children={kids[1]} />
      </Allotment>
      <CollapsedHandle position={options.vertical ? "down" : "right"} visible={hideSashOnSnap === true && !secondPanel.visible} onClick={() => secondPanel && secondPanel.onVisibilityChange && secondPanel.onVisibilityChange(true)} />
    </div>
  );
}

export const ResizablePanelsMemo = React.memo(ResizablePanels, (prev, next) => {
  return prev.firstPanel.visible !== next.firstPanel.visible ||
    prev.secondPanel.visible !== next.secondPanel.visible
});